import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, errorText } from './api'
import * as optimistic from './optimistic'
import type { Member, Tag, Task, User } from './types'
import { useToast } from '../components/ui'

/**
 * Query keys are hierarchical so a live-sync event can invalidate a whole
 * branch — `['board', projectId]` clears every filtered variant of that board
 * without the event needing to know which filters anyone has applied.
 */
export const keys = {
  me: () => ['me'] as const,
  users: () => ['users'] as const,
  tags: () => ['tags'] as const,

  projects: () => ['projects'] as const,
  project: (id: string) => ['projects', id] as const,
  board: (id: string, query = '') => ['board', id, query] as const,
  statuses: (id: string) => ['statuses', id] as const,
  members: (id: string) => ['members', id] as const,

  tasks: (query = '') => ['tasks', query] as const,
  task: (id: string) => ['task', id] as const,
  search: (q: string) => ['search', q] as const,

  prefs: () => ['prefs'] as const,
  tokens: () => ['tokens'] as const,
  slack: () => ['slack'] as const,

  adminUsers: () => ['admin', 'users'] as const,
  invitations: () => ['admin', 'invitations'] as const,
  adminStatus: () => ['admin', 'status'] as const,
  adminSettings: () => ['admin', 'settings'] as const,
}

export function useMe() {
  return useQuery({
    queryKey: keys.me(),
    queryFn: api.me,
    // A failed /me means "not signed in", which is an answer, not an outage.
    retry: false,
    staleTime: 60_000,
  })
}

export function useProjects(archived = false) {
  return useQuery({
    queryKey: [...keys.projects(), archived],
    queryFn: () => api.projects(archived),
  })
}

export function useBoard(projectId: string | undefined, query: string) {
  return useQuery({
    queryKey: keys.board(projectId ?? '', query),
    queryFn: () => api.board(projectId!, query),
    enabled: Boolean(projectId),
    // Keeping the previous board on screen while a filter change loads stops
    // the columns from collapsing and reflowing on every keystroke.
    placeholderData: (prev) => prev,
  })
}

export function useStatuses(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.statuses(projectId ?? ''),
    queryFn: () => api.statuses(projectId!),
    enabled: Boolean(projectId),
  })
}

export function useMembers(projectId: string | undefined) {
  return useQuery({
    queryKey: keys.members(projectId ?? ''),
    queryFn: () => api.members(projectId!),
    enabled: Boolean(projectId),
  })
}

export function useTasks(query: string, enabled = true) {
  return useQuery({
    queryKey: keys.tasks(query),
    queryFn: () => api.tasks(query),
    enabled,
    placeholderData: (prev) => prev,
  })
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: keys.task(id ?? ''),
    queryFn: () => api.task(id!),
    enabled: Boolean(id),
  })
}

export function useTags() {
  return useQuery({ queryKey: keys.tags(), queryFn: api.tags, staleTime: 60_000 })
}

export function useUsers() {
  return useQuery({ queryKey: keys.users(), queryFn: api.users, staleTime: 60_000 })
}

/**
 * Invalidates everything a task change could have affected.
 *
 * Kept for the few operations whose consequences cannot be worked out in the
 * browser — deleting a project, renaming a key, anything that renumbers. The
 * everyday edits below do not use it: they patch the cache and splice in what
 * the server returns, which is what removes the round trip you can feel.
 */
export function useRefreshTasks() {
  const queryClient = useQueryClient()
  return (projectId?: string, taskId?: string) => {
    queryClient.invalidateQueries({ queryKey: ['board'] })
    queryClient.invalidateQueries({ queryKey: ['tasks'] })
    queryClient.invalidateQueries({ queryKey: ['search'] })
    if (projectId) queryClient.invalidateQueries({ queryKey: keys.project(projectId) })
    if (taskId) queryClient.invalidateQueries({ queryKey: keys.task(taskId) })
  }
}

/**
 * Creating a task.
 *
 * The card appears under the cursor before the request leaves, carrying a
 * placeholder id. It is replaced by the real one when the server answers, and
 * removed again if it refuses.
 */
export function useCreateTask() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: api.createTask,

    onMutate: async (body) => {
      await optimistic.haltReads(queryClient)
      const snapshot = optimistic.snapshotTasks(queryClient)
      const id = optimistic.tempId()

      // Enough of a task to render a card. The fields the server decides —
      // key, number, position — are left blank until it answers.
      const draft = {
        id,
        projectId: body.projectId,
        projectKey: '',
        projectName: '',
        number: 0,
        key: '',
        title: body.title ?? '',
        description: body.description ?? '',
        statusId: body.statusId ?? '',
        statusName: body.statusName ?? '',
        statusDone: false,
        position: Number.MAX_SAFE_INTEGER,
        priority: body.priority ?? 'medium',
        assignee: null,
        reporter: null,
        dueDate: body.dueDate ?? null,
        archivedAt: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        tags: [],
        commentCount: 0,
        attachmentCount: 0,
        subtaskCount: 0,
        subtaskDone: 0,
      } as Task

      optimistic.insertTask(queryClient, draft)
      return { snapshot, id }
    },

    onError: (err, _body, context) => {
      if (context) optimistic.restore(queryClient, context.snapshot)
      toast.show('Could not create that task', { tone: 'error', detail: errorText(err) })
    },

    onSuccess: (task, _body, context) => {
      // Swap the placeholder for the real card rather than refetching the
      // board: the server just told us exactly what it created.
      if (context) optimistic.removeTask(queryClient, context.id)
      optimistic.insertTask(queryClient, task)
    },
  })
}

/**
 * Editing a task.
 *
 * Every field in the drawer goes through here, so the drawer answers on the
 * keystroke that closed the editor rather than on the response.
 */
export function useUpdateTask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Parameters<typeof api.updateTask>[1]) =>
      api.updateTask(id, body),

    onMutate: async ({ id, ...body }) => {
      await optimistic.haltReads(queryClient, id)
      const snapshot = optimistic.snapshotTasks(queryClient, id)

      optimistic.patchTask(queryClient, id, (task) => {
        const next = optimistic.applyPatch(task, body as Record<string, unknown>)

        // These need a lookup the plain field copy cannot do: the request
        // carries ids, the card renders names and faces.
        if ('assigneeId' in body) {
          const members = queryClient.getQueryData<Member[]>(keys.members(task.projectId))
          const found = members?.find((m) => m.user.id === body.assigneeId)
          next.assignee = body.assigneeId ? (found?.user ?? task.assignee) : null
        }
        if (body.statusId) {
          const status = optimistic.findStatus(queryClient, body.statusId)
          if (status) Object.assign(next, status)
        }
        if (body.tagIds) {
          const all = queryClient.getQueryData<Tag[]>(keys.tags()) ?? []
          next.tags = body.tagIds
            .map((tagId) => all.find((t) => t.id === tagId))
            .filter((t): t is Tag => Boolean(t))
        }
        return next
      })

      if (body.statusId) {
        const status = optimistic.findStatus(queryClient, body.statusId)
        if (status) optimistic.moveTaskInBoards(queryClient, id, status)
      }
      return { snapshot, id }
    },

    onError: (_err, _vars, context) => {
      if (context) optimistic.restore(queryClient, context.snapshot)
    },

    // The server's version wins, without a second request to go and get it.
    onSuccess: (task) => optimistic.patchTask(queryClient, task.id, () => task),
  })
}

/**
 * Commenting.
 *
 * The comment is in the thread before the request finishes, marked as sending
 * only by a slight fade — a spinner where the text will be is more distracting
 * than the wait it replaces.
 */
export function useAddComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: string; body: string }) =>
      api.addComment(taskId, body),

    onMutate: async ({ taskId, body }) => {
      await optimistic.haltReads(queryClient, taskId)
      const snapshot = optimistic.snapshotTasks(queryClient, taskId)
      const id = optimistic.tempId()
      const me = queryClient.getQueryData<{ user: User }>(keys.me())?.user ?? null

      optimistic.patchComments(queryClient, taskId, (list) => [
        ...list,
        {
          id,
          taskId,
          author: me ? { id: me.id, name: me.name, email: me.email, avatarUrl: me.avatarUrl } : null,
          body,
          editedAt: null,
          createdAt: new Date().toISOString(),
        },
      ])
      return { snapshot, id, taskId }
    },

    onError: (_err, _vars, context) => {
      if (context) optimistic.restore(queryClient, context.snapshot)
    },

    onSuccess: (comment, _vars, context) => {
      optimistic.patchComments(queryClient, comment.taskId, (list) =>
        list.map((c) => (c.id === context?.id ? comment : c)),
      )
    },
  })
}

// --------------------------------------------------------------- subtasks

/**
 * The checklist.
 *
 * Ticking an item is the smallest, most repeated action in the app — somebody
 * working through a task does it a dozen times — so it is the one that must
 * not wait for anything.
 */
export function useAddSubtask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ taskId, title }: { taskId: string; title: string }) =>
      api.addSubtask(taskId, title),

    onMutate: async ({ taskId, title }) => {
      await optimistic.haltReads(queryClient, taskId)
      const snapshot = optimistic.snapshotTasks(queryClient, taskId)
      const id = optimistic.tempId()
      optimistic.patchSubtasks(queryClient, taskId, (list) => [
        ...list,
        {
          id, taskId, title, done: false,
          position: list.length, createdAt: new Date().toISOString(),
        },
      ])
      return { snapshot, id, taskId }
    },
    onError: (_e, _v, context) => context && optimistic.restore(queryClient, context.snapshot),
    onSuccess: (subtask, vars, context) => {
      optimistic.patchSubtasks(queryClient, vars.taskId, (list) =>
        list.map((s) => (s.id === context?.id ? subtask : s)),
      )
    },
  })
}

export function useSetSubtaskDone() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, done }: { id: string; taskId: string; done: boolean }) =>
      api.updateSubtask(id, { done }),

    onMutate: async ({ id, taskId, done }) => {
      await optimistic.haltReads(queryClient, taskId)
      const snapshot = optimistic.snapshotTasks(queryClient, taskId)
      optimistic.patchSubtasks(queryClient, taskId, (list) =>
        list.map((s) => (s.id === id ? { ...s, done } : s)),
      )
      return { snapshot }
    },
    onError: (_e, _v, context) => context && optimistic.restore(queryClient, context.snapshot),
    onSuccess: (subtask, vars) => {
      optimistic.patchSubtasks(queryClient, vars.taskId, (list) =>
        list.map((s) => (s.id === subtask.id ? subtask : s)),
      )
    },
  })
}

export function useRenameSubtask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, title }: { id: string; taskId: string; title: string }) =>
      api.updateSubtask(id, { title }),

    onMutate: async ({ id, taskId, title }) => {
      await optimistic.haltReads(queryClient, taskId)
      const snapshot = optimistic.snapshotTasks(queryClient, taskId)
      optimistic.patchSubtasks(queryClient, taskId, (list) =>
        list.map((s) => (s.id === id ? { ...s, title } : s)),
      )
      return { snapshot }
    },
    onError: (_e, _v, context) => context && optimistic.restore(queryClient, context.snapshot),
  })
}

export function useDeleteSubtask() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; taskId: string }) => api.deleteSubtask(id),

    onMutate: async ({ id, taskId }) => {
      await optimistic.haltReads(queryClient, taskId)
      const snapshot = optimistic.snapshotTasks(queryClient, taskId)
      optimistic.patchSubtasks(queryClient, taskId, (list) => list.filter((s) => s.id !== id))
      return { snapshot }
    },
    onError: (_e, _v, context) => context && optimistic.restore(queryClient, context.snapshot),
  })
}
